import React from "react";
import { Grid } from "@mui/material";
import JobTitleWrapper from "./JobTitleWrapper";
import JobLocationWrapper from "./JobLocationWrapper";
import ExperienceRange from "./ExperienceRange";
// import SalaryRange from "./salaryRange/SalaryRange";
import SalaryRange   from "@foundit/salaryRange/dist/SalaryRange";
import {ISalaryRange}  from "@foundit/salaryRange/dist/types/salaryRange";
import "@foundit/salaryRange/dist/SalaryRange.css";
import CampaignName from "./CampaignName";
import SelectCandidate from "./SelectCandidate";
import CampaignPreferences from "./CampaignPreferences";
import SaveInFolder from "./SaveInFolder";
import {
  ICampaignContactableDetails,
  ICurrencyList,
  IExperienceRange,
  IFolderDetails,
  IJobLocations,
  ILocationResponse,
  TJobMode,
  TRequestStatus,
} from "../../../../../types/campaign.types";
import NoticePeriod from "./NoticePeriod";

interface IQueryParamsItems {
  triggeredFromPage: string;
  isBulkCampaign: boolean;
}
export interface IJobTitleDetails {
  jobTitle: string;
  handleUpdateJobTitle: (newJobTitle: string) => void;
  error: string;
}

export interface IJobLocationDetails {
  jobLocations: ILocationResponse | null;
  selectedJobLocations: IJobLocations[];
  handleUpdateJobLocations: (selectedLocations: IJobLocations[]) => void;
  error: string;
}

export interface IJobModeDetails {
  jobMode: TJobMode;
  handleUpdateJobMode: (jobMode: string) => void;
}

export interface IExperienceRangeDetails {
  experienceRange: IExperienceRange;
  handleUpdateExperienceRange: (experienceRange: IExperienceRange) => void;
  error: string;
}

export interface ISalaryRangeDetails {
  salaryRange: ISalaryRange;
  handleUpdateSalaryRange: (salaryRange: ISalaryRange) => void;
  currencyList: ICurrencyList[];
  isSeaAndGulf: boolean;
  error: string;
  handleBlur?: ()=>void
}

export interface ICampaignNameDetails {
  campaignName: string;
  handleUpdateCampaignName: (campaignName: string) => void;
  error: string;
}

export interface ISelectedFolderDetails extends IQueryParamsItems {
  selectedFolder: IFolderDetails | null;
  handleUpdateSelectedFolder: (selectedFolder: IFolderDetails | null) => void;
  error: string;
  folderId: number | null;
  folders: IFolderDetails[];
  fetchFoldersStatus: TRequestStatus;
}

export interface INumberOfSelectedCandidates extends IQueryParamsItems {
  numberOfSelectedCandidates: string;
  handleUpdateNumberOfSelectedCandidates: (
    numberOfSelectedCandidates: string
  ) => void;
  error: string;
  campaignContactableDetails: ICampaignContactableDetails;
}

export interface ICampaignPreferencesDetails extends IQueryParamsItems {
  isSendToAlreadyContacted: boolean;
  handleUpdateIsSendToAlreadyContacted: (
    isSendToAlreadyContacted: boolean
  ) => void;
}

export interface INoticePeriodDetails {
  noticePeriod: string;
  handleUpdateNoticePeriod: (noticePeriod: string) => void;
  error: string;
}

interface IProps {
  jobTitleDetails: IJobTitleDetails;
  jobLocationDetails: IJobLocationDetails;
  jobModeDetails: IJobModeDetails;
  experienceRangeDetails: IExperienceRangeDetails;
  salaryRangeDetails: ISalaryRangeDetails;
  campaignNameDetails: ICampaignNameDetails;
  selectedFolderDetails: ISelectedFolderDetails;
  numberOfSelectedCandidatesDetails: INumberOfSelectedCandidates;
  campaignPreferencesDetails: ICampaignPreferencesDetails;
  noticePeriodDetails: INoticePeriodDetails;
}

const CampaignSetupForm: React.FC<IProps> = ({
  jobTitleDetails,
  jobLocationDetails,
  jobModeDetails,
  experienceRangeDetails,
  salaryRangeDetails,
  campaignNameDetails,
  campaignPreferencesDetails,
  numberOfSelectedCandidatesDetails,
  selectedFolderDetails,
  noticePeriodDetails,
}) => {
  return (
    <Grid container spacing={3}>
      {/* Job Title */}
      <Grid item xs={6}>
        <JobTitleWrapper
          jobTitle={jobTitleDetails.jobTitle}
          handleUpdateJobTitle={jobTitleDetails.handleUpdateJobTitle}
          error={jobTitleDetails.error}
        />
      </Grid>

      {/* Job Mode & Location */}
      <Grid item xs={6}>
        <JobLocationWrapper
          jobMode={jobModeDetails.jobMode}
          jobLocations={jobLocationDetails.jobLocations}
          selectedJobLocations={jobLocationDetails.selectedJobLocations}
          handleUpdateJobLocations={jobLocationDetails.handleUpdateJobLocations}
          handleUpdateJobMode={jobModeDetails.handleUpdateJobMode}
          error={jobLocationDetails.error}
        />
      </Grid>

      {/* Experience Range */}
      <Grid item xs={6}>
        <ExperienceRange
          experienceRange={experienceRangeDetails.experienceRange}
          handleUpdateExperienceRange={
            experienceRangeDetails.handleUpdateExperienceRange
          }
          error={experienceRangeDetails.error}
        />
      </Grid>

      {/* Salary Range */}
      <Grid item xs={6}>
        <SalaryRange
          handleUpdateSalaryRange={salaryRangeDetails.handleUpdateSalaryRange}
          salaryRange={salaryRangeDetails.salaryRange}
          currencyList={salaryRangeDetails.currencyList}
          error={salaryRangeDetails.error}
          onBlur={salaryRangeDetails?.handleBlur}
        />
      </Grid>

      {/* Campaign Name */}
      <Grid item xs={6}>
        <CampaignName
          handleUpdateCampaignName={
            campaignNameDetails.handleUpdateCampaignName
          }
          campaignName={campaignNameDetails.campaignName}
          error={campaignNameDetails.error}
        />
      </Grid>

      {/* Save In Folder */}
      <Grid item xs={6}>
        <SaveInFolder
          handleUpdateSelectedFolder={
            selectedFolderDetails.handleUpdateSelectedFolder
          }
          selectedFolder={selectedFolderDetails.selectedFolder}
          error={selectedFolderDetails.error}
          triggeredFromPage={selectedFolderDetails.triggeredFromPage}
          isBulkCampaign={selectedFolderDetails.isBulkCampaign}
          folderId={selectedFolderDetails.folderId}
          folders={selectedFolderDetails.folders}
          fetchFoldersStatus={selectedFolderDetails.fetchFoldersStatus}
        />
      </Grid>

      {/* Select Candidate */}
      <Grid item xs={6}>
        <SelectCandidate
          handleUpdateNumberOfSelectedCandidates={
            numberOfSelectedCandidatesDetails.handleUpdateNumberOfSelectedCandidates
          }
          numberOfSelectedCandidates={
            numberOfSelectedCandidatesDetails.numberOfSelectedCandidates
          }
          error={numberOfSelectedCandidatesDetails.error}
          triggeredFromPage={
            numberOfSelectedCandidatesDetails.triggeredFromPage
          }
          isBulkCampaign={numberOfSelectedCandidatesDetails.isBulkCampaign}
          campaignContactableDetails={
            numberOfSelectedCandidatesDetails.campaignContactableDetails
          }
        />
      </Grid>

      {/* Notice Period  */}
      <Grid item xs={6}>
        <NoticePeriod
          noticePeriod={noticePeriodDetails?.noticePeriod || ""}
          handleUpdateNoticePeriod={
            noticePeriodDetails.handleUpdateNoticePeriod
          }
          error={noticePeriodDetails?.error || ""}
        />
      </Grid>

      {/* Other Preference */}
      <Grid item xs={12} sx={{ paddingTop: "0px !important" }}>
        <CampaignPreferences
          handleUpdateIsSendToAlreadyContacted={
            campaignPreferencesDetails.handleUpdateIsSendToAlreadyContacted
          }
          isSendToAlreadyContacted={
            campaignPreferencesDetails.isSendToAlreadyContacted
          }
          triggeredFromPage={campaignPreferencesDetails.triggeredFromPage}
          isBulkCampaign={campaignPreferencesDetails.isBulkCampaign}
        />
      </Grid>
    </Grid>
  );
};

export default CampaignSetupForm;
